import {
  AttendeeId,
  MeetingAttemptV3,
  MeetingAttendees,
  UserAttendeeId,
} from '@chilipiper/api-client/src/user-availability'
import {
  AlwaysInvitedUser,
  PublicGroupSchedulingLink,
} from '@chilipiper/api-client/src/team-scheduling'
import { unique } from 'remeda'
import { LinkResourceEnhanced, isGroupSchedulingLinkEnhanced } from '../../load-meeting-attempt'

export const isInternalUser = (attendeeId: AttendeeId): attendeeId is UserAttendeeId =>
  attendeeId.type === 'UserAttendeeId'

export const getAdditionalRequiredAttendeesId = (attendees?: MeetingAttendees) => {
  if (!attendees || attendees.type === 'NoAdditionalAttendees') {
    return []
  }

  return attendees.attendees.reduce((acc, attendee) => {
    if (isInternalUser(attendee.id) && !attendee.optional) {
      acc.push(attendee.id.userId)
    }
    return acc
  }, [] as string[])
}

export const getAdditionalRequiredAttendeesWithFallback = ({
  meetingAttemptAttendees,
  fallbackAttendees = [],
}: {
  meetingAttemptAttendees?: MeetingAttendees
  fallbackAttendees?: AlwaysInvitedUser[]
}) => {
  const meetingAttemptAdditionalRequiredAttendees =
    getAdditionalRequiredAttendeesId(meetingAttemptAttendees)

  const attendees = fallbackAttendees
    .filter(attendees => !attendees.optional)
    .map(attendee => attendee.userId)

  return meetingAttemptAttendees ? meetingAttemptAdditionalRequiredAttendees : attendees
}

export const getRequiredUsersGroupFromLink = (
  link?: LinkResourceEnhanced & { type: 'GroupSchedulingLink' },
  attempt?: MeetingAttemptV3
) => {
  if (!link) {
    return []
  }
  const meetingAttemptAdditionalRequiredAttendees = getAdditionalRequiredAttendeesId(
    attempt?.additionalAttendees
  )

  const linkRequiredAttendees = link.requiredAttendees?.map(attendee => attendee.userId) || []

  const requiredAttendees =
    meetingAttemptAdditionalRequiredAttendees.length > 0
      ? meetingAttemptAdditionalRequiredAttendees
      : linkRequiredAttendees

  return requiredAttendees
}

export const getRequiredUsersFromGroupLinkResource = (
  groupSchedulingLink?: LinkResourceEnhanced,
  attempt?: MeetingAttemptV3
) => {
  if (!groupSchedulingLink || !isGroupSchedulingLinkEnhanced(groupSchedulingLink)) {
    return []
  }

  const requiredAttendees = getRequiredUsersGroupFromLink(groupSchedulingLink, attempt)

  // If there are no required attendees we should add the host as a required attendee even if he is optional
  const hostId =
    !groupSchedulingLink.isHostOptional || requiredAttendees.length === 0
      ? groupSchedulingLink.hostId
      : undefined

  return [...(hostId ? [hostId] : []), ...requiredAttendees]
}

export const getRequiredAttendeesFromGroupLink = (
  groupSchedulingLink?: PublicGroupSchedulingLink,
  attempt?: MeetingAttemptV3
) => {
  if (!groupSchedulingLink) {
    return []
  }

  const meetingAttemptAdditionalRequiredAttendees = unique(
    getAdditionalRequiredAttendeesId(attempt?.additionalAttendees)
  )

  const groupRequiredAttendees = unique(
    groupSchedulingLink.additionalInvitedUsers
      ?.filter(attendee => !attendee.optional)
      .map(attendee => attendee.userId) || []
  )

  return meetingAttemptAdditionalRequiredAttendees.length > 0
    ? meetingAttemptAdditionalRequiredAttendees
    : groupRequiredAttendees
}

export const getGroupHeaderUsers = (
  groupSchedulingLink: PublicGroupSchedulingLink,
  attempt?: MeetingAttemptV3
) => {
  if (!groupSchedulingLink) {
    return []
  }

  const hostId = groupSchedulingLink.host.userId

  if (attempt?.additionalAttendees) {
    return [hostId, ...getAdditionalRequiredAttendeesId(attempt.additionalAttendees)]
  }

  return getRequiredAttendeesFromGroupLink(groupSchedulingLink, attempt)
}

export const getGroupEnhancedHeaderUsers = (
  groupSchedulingLink: LinkResourceEnhanced,
  attempt?: MeetingAttemptV3
) => {
  if (!isGroupSchedulingLinkEnhanced(groupSchedulingLink)) {
    return []
  }

  if (attempt?.additionalAttendees) {
    return [
      groupSchedulingLink.hostId,
      ...getAdditionalRequiredAttendeesId(attempt.additionalAttendees),
    ]
  }

  return getRequiredUsersGroupFromLink(groupSchedulingLink, attempt)
}

// ============================================================================
// Enhanced Functions (using LinkResourceEnhanced)
// ============================================================================

/**
 * Get header users for a group scheduling link (Enhanced version).
 *
 * @param linkResource - Enhanced link resource with hostId already resolved
 * @param attempt - Optional meeting attempt with additional attendees
 */
export const getGroupHeaderUsersEnhanced = (
  linkResource: LinkResourceEnhanced,
  attempt?: MeetingAttemptV3
): string[] => {
  if (!isGroupSchedulingLinkEnhanced(linkResource)) {
    return []
  }

  if (attempt?.additionalAttendees) {
    return unique([
      linkResource.hostId,
      ...getAdditionalRequiredAttendeesId(attempt.additionalAttendees),
    ])
  }

  return unique([linkResource.hostId, ...getRequiredUsersGroupFromLink(linkResource, attempt)])
}

/**
 * Get required users for a group scheduling link (Enhanced version).
 *
 * @param linkResource - Enhanced link resource with hostId already resolved
 * @param attempt - Optional meeting attempt
 */
export const getRequiredUsersFromGroupLinkEnhanced = (
  linkResource?: LinkResourceEnhanced,
  attempt?: MeetingAttemptV3
): string[] => {
  if (!linkResource || !isGroupSchedulingLinkEnhanced(linkResource)) {
    return []
  }

  const requiredAttendees = getRequiredUsersFromGroupLinkResource(linkResource, attempt)

  return requiredAttendees
}
